// =============================================================================
// pages/Settings.js — Sistem Ayarları (Faz G)
// =============================================================================
// Entegrasyon durumları + env eksikliği gösterir. Worker durumları + DB istatistikleri.
// Her ortam değişkeni için ne olur sayar (impact).
// =============================================================================

import React, { useEffect, useState } from "react";
import {
  Box, Typography, Grid, Card, CardContent, Stack, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  LinearProgress, IconButton, Tooltip
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import { getSystemStatus } from "../services/api";

const PRIORITY_COLOR = {
  kritik: 'error',
  yüksek: 'warning',
  orta: 'info',
  düşük: 'default'
};

export default function Settings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getSystemStatus()
      .then(r => setStatus(r.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <Box p={3}><LinearProgress /></Box>;
  if (!status) return <Box p={3}><Alert severity="error">Yüklenemedi</Alert></Box>;

  const { integrations, workers, db_stats, summary, version, uptime_seconds } = status;
  const uptimeHours = Math.floor(uptime_seconds / 3600);

  return (
    <Box sx={{ p: 2 }}>
      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#1565C0' }}>
            ⚙️ Sistem Ayarları
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Versiyon: {version} · Uptime: {uptimeHours} saat · Entegrasyonlar + worker durumları
          </Typography>
        </Box>
        <IconButton onClick={load} color="primary"><RefreshIcon /></IconButton>
      </Stack>

      {/* ÖZET KARTLARI */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderTop: '3px solid #10b981' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Aktif Entegrasyon</Typography>
              <Typography variant="h4" fontWeight={800} color="success.main">
                {summary.active_integrations}/{summary.total_integrations}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderTop: '3px solid #ef4444' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Kritik Bekleyen</Typography>
              <Typography variant="h4" fontWeight={800} color="error.main">
                {summary.critical_pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderTop: '3px solid #1565C0' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Aktif Worker</Typography>
              <Typography variant="h4" fontWeight={800} color="primary.main">
                {summary.active_workers}/{workers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderTop: '3px solid #7c3aed' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">DB Toplam Sipariş</Typography>
              <Typography variant="h4" fontWeight={800} sx={{ color: '#7c3aed' }}>
                {db_stats.total_orders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {summary.critical_pending > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>{summary.critical_pending} kritik entegrasyon credentials bekliyor</strong> —
          PayTR ve Bizimhesap aktivasyonu olmadan kart ödeme + e-fatura çalışmaz.
        </Alert>
      )}

      {/* ENTEGRASYONLAR */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} mb={2}>🔌 Entegrasyonlar</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Servis</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Öncelik</TableCell>
                  <TableCell>Etki</TableCell>
                  <TableCell>Env Değişkenleri</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {integrations.map(i => (
                  <TableRow key={i.key} hover>
                    <TableCell><strong>{i.name}</strong></TableCell>
                    <TableCell>
                      {i.active
                        ? <Chip icon={<CheckCircleIcon />} label="Aktif" color="success" size="small" />
                        : <Chip icon={<ErrorIcon />} label="Pasif" color="default" size="small" variant="outlined" />}
                      {i.test_mode && <Chip label="TEST" color="warning" size="small" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell><Chip label={i.priority} color={PRIORITY_COLOR[i.priority]} size="small" /></TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{i.impact}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                      {i.env_vars.join(', ')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* WORKER'LAR */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} mb={2}>⚙️ Arka Plan Worker'ları</Typography>
          <Grid container spacing={1.5}>
            {workers.map(w => (
              <Grid item xs={12} md={6} key={w.key}>
                <Stack direction="row" spacing={1.5} alignItems="center"
                  sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                  {w.active
                    ? <CheckCircleIcon color="success" />
                    : <ErrorIcon color="error" />}
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>{w.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{w.desc}</Typography>
                  </Box>
                  <Chip label={w.tick} size="small" variant="outlined" />
                </Stack>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* DB İSTATİSTİKLERİ */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={700} mb={2}>📊 Veri Tabanı İstatistikleri</Typography>
          <Grid container spacing={2}>
            {[
              { label: 'Toplam Sipariş', value: db_stats.total_orders },
              { label: 'Toplam Ürün', value: db_stats.total_products },
              { label: 'Onaylı Müşteri', value: db_stats.approved_users },
              { label: 'Onay Bekleyen', value: db_stats.pending_users },
              { label: 'Aktif Email Kontak', value: db_stats.active_email_contacts },
              { label: 'Aktif Kupon', value: db_stats.active_coupons },
              { label: 'Aktif Segment', value: db_stats.active_segments },
              { label: 'Aktif Drip Kampanya', value: db_stats.active_drip_campaigns }
            ].map((s, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Box sx={{ textAlign: 'center', p: 1.5, border: '1px solid #f1f5f9', borderRadius: 1 }}>
                  <Typography variant="h4" fontWeight={800} color="primary.main">{s.value || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
